"use client";

import { usePathname } from "next/navigation";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

interface IBreadcrumbContextType {
  "breadcrumbs": { title: string; href: string }[];
  "registerTitle": (
    id: string,
    title: string,
    parentId: string | null,
    template?: string | null,
    setDocumentTitle?: boolean,
    showOnlyAsCurrent?: boolean,
  ) => void;
  "unregisterTitle": (id: string) => void;
  "updateCurrentPathname": (pathname: string) => void;
}

/**
 * Reconstructs a hierarchical list of breadcrumb titles from a given map of
 * titles and their parent-child relationships, calculating correct URLs.
 *
 * @param map - A `Map` where the keys are unique IDs and the values are
 * objects containing the title to be included in the breadcrumb hierarchy
 * and the ID of the parent title, if any.
 *
 * @param currentPathname - The current page pathname to calculate ancestor
 * URLs from.
 *
 * @returns A list of breadcrumb objects with titles and URLs in
 * hierarchical order.
 */
function reconstructHierarchy(
  map: Map<
    string,
    {
      "title": string;
      "parentId": string | null;
      "template"?: string | null;
      "showOnlyAsCurrent"?: boolean;
    }
  >,
  currentPathname: string,
): { title: string; href: string }[] {
  const result: { title: string; href: string; depth: number }[] = [];
  const visited = new Set<string>();

  const pathSegments = currentPathname
    .split("/")
    .filter((segment) => segment !== "");

  // Find all leaf nodes (nodes that are not parents of any other node):
  const allParentIds = new Set(
    Array.from(map.values())
      .map((entry) => entry.parentId)
      .filter((parentId) => parentId !== null),
  );

  const leafNodes = Array.from(map.keys()).filter(
    (id) => !allParentIds.has(id),
  );

  const addTitle = (id: string, isTopLevel = false): number => {
    if (visited.has(id)) {
      return 0;
    }

    visited.add(id);
    const entry = map.get(id);

    if (!entry) {
      return 0;
    }

    let depth = 0;

    // Recursively add parent first and get its depth:
    if (entry.parentId !== null && !visited.has(entry.parentId)) {
      depth = addTitle(entry.parentId, false) + 1;
    }

    // Only add this title if it's the top-level breadcrumb (isTopLevel = true),
    // or showOnlyAsCurrent is not set to true:
    if (isTopLevel || entry.showOnlyAsCurrent !== true) {
      result.push({ title: entry.title, href: "", depth });
    }

    return depth;
  };

  // Build the hierarchy first to get depths:
  for (const leafId of leafNodes) {
    addTitle(leafId, true);
  }

  // Now calculate URLs based on depths and return cleaned results:
  return result.map((item) => {
    // Depth 0 corresponds to root ("/") which has no path segment
    const hrefSegments = pathSegments.slice(0, Math.max(0, item.depth));
    const href = "/" + hrefSegments.join("/");

    return { title: item.title, href: href === "/" ? "/" : href };
  });
}

const BreadcrumbContext = createContext<IBreadcrumbContextType | null>(null);

/**
 * Provider component that manages the breadcrumb navigation state.
 *
 * @param params - Parameters passed from the parent component.
 *
 * @returns A React element containing the provider and its children.
 */
export function BreadcrumbProvider({
  children,
}: {
  "children": ReactNode;
}): React.ReactElement {
  const [breadcrumbs, setBreadcrumbs] = useState<
    { title: string; href: string }[]
  >([]);

  const [currentPathname, setCurrentPathname] = useState<string>("");

  const titlesMap = useRef<
    Map<
      string,
      {
        "title": string;
        "parentId": string | null;
        "template"?: string | null;
        "showOnlyAsCurrent"?: boolean;
      }
    >
  >(new Map());

  const updateCurrentPathname = useCallback((pathname: string) => {
    setCurrentPathname(pathname);
    if (titlesMap.current.size > 0) {
      const sortedBreadcrumbs = reconstructHierarchy(
        titlesMap.current,
        pathname,
      );

      setBreadcrumbs(sortedBreadcrumbs);
    }
  }, []);

  const registerTitle = useCallback(
    (
      id: string,
      title: string,
      parentId: string | null = null,
      template: string | null = null,
      setDocumentTitle?: boolean,
      showOnlyAsCurrent = false,
    ) => {
      titlesMap.current.set(id, {
        title,
        parentId,
        template,
        showOnlyAsCurrent,
      });

      if (currentPathname) {
        const sortedBreadcrumbs = reconstructHierarchy(
          titlesMap.current,
          currentPathname,
        );
        setBreadcrumbs(sortedBreadcrumbs);
      }

      if (setDocumentTitle === true) {
        // If template is null/undefined, try to use parent's template:
        if (template === null && parentId !== null) {
          const parentEntry = titlesMap.current.get(parentId);
          template = parentEntry?.template ?? null;
        }

        if (typeof document !== "undefined") {
          document.title =
            template !== null ? template.replace("%s", title) : title;
        }
      }
    },
    [currentPathname],
  );

  const unregisterTitle = useCallback(
    (id: string) => {
      titlesMap.current.delete(id);
      if (currentPathname) {
        const sortedBreadcrumbs = reconstructHierarchy(
          titlesMap.current,
          currentPathname,
        );
        setBreadcrumbs(sortedBreadcrumbs);
      }
    },
    [currentPathname],
  );

  const contextValue = useMemo(
    () => ({
      breadcrumbs,
      registerTitle,
      unregisterTitle,
      updateCurrentPathname,
    }),
    [breadcrumbs, registerTitle, unregisterTitle, updateCurrentPathname],
  );

  return (
    <BreadcrumbContext.Provider value={contextValue}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

/**
 * Hook to access the BreadcrumbContext within a component.
 *
 * @throws Error if used outside of a BreadcrumbProvider.
 *
 * @returns The BreadcrumbContext object.
 */
export function useBreadcrumb(): IBreadcrumbContextType {
  const context = useContext(BreadcrumbContext);

  if (!context) {
    throw new Error("useBreadcrumb must be used within a BreadcrumbProvider");
  }

  return context;
}

export const ParentIdContext = createContext<string | null>(null);

/**
 * Provides metadata (title and index) for a specific section of the application.
 *
 * @param props - Props passed from the parent component.
 *
 * @returns The JSX element for the MetadataProvider component.
 */
export function MetadataProvider({
  children,
  title,
  template,
  showOnlyAsCurrent = false,
}: {
  "children": ReactNode;
  "title"?: string;
  "template"?: string;
  "showOnlyAsCurrent"?: boolean;
}): React.ReactElement {
  const { registerTitle, unregisterTitle, updateCurrentPathname } =
    useBreadcrumb();
  const pathname = usePathname();
  const id = useId();
  const parentId = useContext(ParentIdContext);
  const resolvedTitle = title ?? "Unknown";
  const resolvedTemplate = template ?? "Unknown";

  // Update the current pathname when this component mounts or pathname changes
  useEffect(() => {
    updateCurrentPathname(pathname);
  }, [pathname, updateCurrentPathname]);

  useEffect(() => {
    registerTitle(
      id,
      resolvedTitle,
      parentId,
      resolvedTemplate,
      false,
      showOnlyAsCurrent,
    );

    return () => {
      unregisterTitle(id);
    };
  }, [
    id,
    resolvedTitle,
    resolvedTemplate,
    parentId,
    registerTitle,
    unregisterTitle,
    showOnlyAsCurrent,
  ]);

  return (
    <ParentIdContext.Provider value={id}>{children}</ParentIdContext.Provider>
  );
}
